import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Badge } from './badge';
import { Button } from './button';
import { Label } from './label';
import { cn } from '../../utils/cn';

const MultiSelect = React.forwardRef(
    (
        {
            className,
            label,
            description,
            placeholder = 'Select items...',
            searchPlaceholder = 'Search...',
            emptyText = 'No items found.',
            options = [],
            value = [],
            onChange,
            disabled = false,
            maxItems,
            ...props
        },
        ref,
    ) => {
        const [open, setOpen] = React.useState(false);
        const [searchValue, setSearchValue] = React.useState('');

        const handleSelect = (optionValue) => {
            const newValue = value.includes(optionValue)
                ? value.filter((v) => v !== optionValue)
                : maxItems && value.length >= maxItems
                  ? value
                  : [...value, optionValue];

            onChange?.(newValue);
        };

        const handleRemove = React.useCallback(
            (optionValue, e) => {
                e.stopPropagation();
                onChange?.(value.filter((v) => v !== optionValue));
            },
            [onChange, value],
        );

        const selectedOptions = options.filter((opt) =>
            value.includes(opt.value),
        );

        const displayValue = React.useMemo(() => {
            if (selectedOptions.length === 0) {
                return (
                    <span className="text-muted-foreground">{placeholder}</span>
                );
            }

            if (selectedOptions.length <= 2) {
                return (
                    <div className="flex gap-1 flex-wrap">
                        {selectedOptions.map((option) => (
                            <Badge
                                key={option.value}
                                variant="secondary"
                                interactive
                                className="h-6 px-1.5"
                                onClick={(e) => handleRemove(option.value, e)}
                            >
                                <span className="text-xs">{option.label}</span>
                                <X className="h-3 w-3 text-muted-foreground" />
                            </Badge>
                        ))}
                    </div>
                );
            }

            return (
                <div className="flex items-center gap-1">
                    <Badge
                        variant="secondary"
                        interactive
                        className="h-6 px-1.5"
                        onClick={(e) =>
                            handleRemove(selectedOptions[0].value, e)
                        }
                    >
                        <span className="text-xs">
                            {selectedOptions[0].label}
                        </span>
                        <X className="h-3 w-3 text-muted-foreground" />
                    </Badge>
                    <Badge variant="secondary" className="h-6 px-1.5">
                        <span className="text-xs">
                            +{selectedOptions.length - 1} more
                        </span>
                    </Badge>
                </div>
            );
        }, [selectedOptions, placeholder, handleRemove]);

        return (
            <div className="flex flex-col gap-2 w-full">
                {label && (
                    <Label
                        htmlFor={`multiselect-${label}`}
                        className="text-sm font-medium text-foreground"
                    >
                        {label}
                    </Label>
                )}
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            id={label ? `multiselect-${label}` : undefined}
                            ref={ref}
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            aria-haspopup="listbox"
                            disabled={disabled}
                            className={cn(
                                'w-full h-9 justify-between font-normal bg-background border-input px-4 py-2 text-sm',
                                'hover:bg-background hover:border-input',
                                'focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0',
                                !value.length && 'text-muted-foreground',
                                className,
                            )}
                            {...props}
                        >
                            <div className="flex-1 text-left overflow-hidden">
                                {displayValue}
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[250px] p-0 border-border bg-popover"
                        align="start"
                    >
                        <Command className="rounded-md bg-popover">
                            <CommandInput
                                placeholder={searchPlaceholder}
                                className="h-9 text-sm"
                                value={searchValue}
                                onValueChange={setSearchValue}
                            />
                            <CommandList className="max-h-[240px]">
                                <CommandEmpty className="py-3 text-center text-sm text-muted-foreground">
                                    {emptyText}
                                </CommandEmpty>
                                <CommandGroup className="p-1">
                                    {options.map((option) => {
                                        const isSelected = value.includes(
                                            option.value,
                                        );
                                        const isDisabled =
                                            maxItems &&
                                            value.length >= maxItems &&
                                            !isSelected;

                                        return (
                                            <CommandItem
                                                key={option.value}
                                                value={option.label}
                                                disabled={isDisabled}
                                                onSelect={() => {
                                                    handleSelect(option.value);
                                                }}
                                                className={cn(
                                                    'group flex items-center gap-2 px-2 py-1 text-sm rounded-sm cursor-pointer transition-colors',
                                                    isDisabled &&
                                                        'opacity-50 cursor-not-allowed',
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        'h-4 w-4 rounded-[4px] border flex items-center justify-center transition-colors',
                                                        isSelected
                                                            ? 'bg-primary border-primary'
                                                            : 'border-input group-data-[selected=true]:border-muted-foreground/70',
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <Check className="h-3 w-3 text-primary-foreground" />
                                                    )}
                                                </div>
                                                <span
                                                    className={cn(
                                                        isSelected &&
                                                            'font-medium',
                                                    )}
                                                >
                                                    {option.label}
                                                </span>
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                {description && (
                    <p className="text-sm text-muted-foreground">
                        {description}
                    </p>
                )}
            </div>
        );
    },
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
